import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { BadgeTrackLabels } from '../../models/badge';
import { GamificationResult } from '../../models/gamification';
import { Match } from '../../models/match';
import { User } from '../../models/user';

const SIZE = { width: 1080, height: 1350 };

// Draws the shareable match recap on a canvas and hands it to the OS share sheet.
@Injectable({
	providedIn: 'root'
})
export class RecapCardService {

	async share(match: Match, result: GamificationResult): Promise<void> {
		const blob = await this.render(match, result);
		const file = new File([blob], 'recap.png', { type: 'image/png' });

		if (Capacitor.isNativePlatform()) {
			const data = await this.toBase64(blob);
			const saved = await Filesystem.writeFile({ path: 'recap.png', data, directory: Directory.Cache });
			await Share.share({ title: 'Pro Pool', files: [saved.uri] });
		} else if (navigator.canShare?.({ files: [file] })) {
			await navigator.share({ files: [file], title: 'Pro Pool' });
		} else {
			// No file sharing (Web Share Level 2) available: save the image instead.
			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = 'recap.png';
			link.click();
			URL.revokeObjectURL(link.href);
		}
	}

	private async render(match: Match, result: GamificationResult): Promise<Blob> {
		const canvas = document.createElement('canvas');
		canvas.width = SIZE.width;
		canvas.height = SIZE.height;
		const ctx = canvas.getContext('2d');
		const css = getComputedStyle(document.documentElement);
		const token = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
		const font = token('--pp-font-family', 'Poppins, sans-serif');
		const primary = token('--pp-text-primary', '#F5F3F7');
		const secondary = token('--pp-text-secondary', '#9A93A8');

		ctx.fillStyle = token('--pp-bg', '#090617');
		ctx.fillRect(0, 0, SIZE.width, SIZE.height);
		const glow = ctx.createLinearGradient(0, 0, SIZE.width, SIZE.height);
		glow.addColorStop(0, token('--pp-gradient-start', '#FF1B8D'));
		glow.addColorStop(1, token('--pp-gradient-end', '#9B2FE0'));
		ctx.globalAlpha = 0.25;
		ctx.fillStyle = glow;
		ctx.fillRect(0, 0, SIZE.width, 520);
		ctx.globalAlpha = 1;

		ctx.textAlign = 'center';
		ctx.fillStyle = secondary;
		ctx.font = `600 40px ${font}`;
		ctx.fillText('PRO POOL', SIZE.width / 2, 110);

		// Players: me/opponent are only mapped for the player's own matches; a tournament match the organiser
		// scores has just challenger/challenged (or their teams for doubles).
		const mine = !!match.me;
		const players = mine ? [match.me, match.opponent] : [match.challenger ?? match.challengerTeam, match.challenged ?? match.challengedTeam];
		const score = mine ? [match.myScore, match.opponentScore] : [match.scoreChallenger, match.scoreChallenged];
		for (const [user, x] of [[players[0], 270], [players[1], 810]] as [User, number][]) {
			await this.drawAvatar(ctx, user, x, 330, 110, token('--pp-surface-alt', '#1E1829'), primary);
			ctx.fillStyle = primary;
			ctx.font = `600 40px ${font}`;
			ctx.fillText(this.fit(ctx, this.name(user), 420), x, 510);
		}

		// Final score
		ctx.fillStyle = primary;
		ctx.font = `700 190px ${font}`;
		ctx.fillText(`${score[0]} - ${score[1]}`, SIZE.width / 2, 790);

		let y = 900;
		if (result?.ratingDelta) {
			ctx.fillStyle = result.ratingDelta > 0 ? token('--ion-color-success', '#2dd36f') : token('--ion-color-danger', '#eb445a');
			ctx.font = `700 64px ${font}`;
			ctx.fillText(`${result.ratingDelta > 0 ? '+' : ''}${result.ratingDelta} rating`, SIZE.width / 2, y);
			y += 90;
		}
		ctx.fillStyle = token('--ion-color-warning', '#ffc409');
		ctx.font = `600 44px ${font}`;
		for (const badge of (result?.unlockedBadges ?? []).slice(0, 3)) {
			ctx.fillText(`🏅 ${BadgeTrackLabels[badge.track]}`, SIZE.width / 2, y);
			y += 68;
		}

		// Venue and date
		const date = new Date(match.endDate || match.startDate || Date.now()).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
		ctx.fillStyle = secondary;
		ctx.font = `500 40px ${font}`;
		ctx.fillText([match.placeName, date].filter(Boolean).join(' · '), SIZE.width / 2, 1260);

		return new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(new Error('Recap render failed')), 'image/png'));
	}

	private async drawAvatar(ctx: CanvasRenderingContext2D, user: User, x: number, y: number, r: number, fallbackFill: string, textColor: string): Promise<void> {
		const img = await this.loadImage(user?.profilePhoto || './assets/images/defaultavatar.png');
		ctx.save();
		ctx.beginPath();
		ctx.arc(x, y, r, 0, Math.PI * 2);
		ctx.clip();
		if (img) {
			ctx.drawImage(img, x - r, y - r, r * 2, r * 2);
		} else {
			ctx.fillStyle = fallbackFill;
			ctx.fillRect(x - r, y - r, r * 2, r * 2);
			ctx.fillStyle = textColor;
			ctx.font = '700 96px sans-serif';
			ctx.textBaseline = 'middle';
			ctx.fillText(this.name(user).charAt(0).toUpperCase(), x, y);
		}
		ctx.restore();
		ctx.textBaseline = 'alphabetic';
	}

	// Resolves null on failure (e.g. a CORS-blocked avatar) so the card still renders with an initial.
	private loadImage(src: string): Promise<HTMLImageElement | null> {
		return new Promise(resolve => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = () => resolve(null);
			img.src = src;
		});
	}

	private name(user: User): string {
		return String(user?.name || user?.username || '?');
	}

	private fit(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
		while (text.length > 1 && ctx.measureText(text).width > maxWidth) {
			text = text.slice(0, -2) + '…';
		}
		return text;
	}

	private toBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve((reader.result as string).split(',')[1]);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(blob);
		});
	}
}
